import React, { useEffect, useState } from 'react';
import { getGroupsMembersReport, downloadGroupsMembersReportCsv, transferGroupOwnership } from '../../services/adminService';

const AdminMembersReport = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [transferringId, setTransferringId] = useState(null);

  const fetchReport = async () => {
    try {
      const data = await getGroupsMembersReport();
      setGroups(data.groups);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  const handleTransfer = async (group, member) => {
    const confirmed = window.confirm(
      `Make ${member.name} the new owner of "${group.groupName}"? ${group.members.find((m) => m.role === 'owner')?.name || 'The current owner'} will become a regular member.`
    );
    if (!confirmed) return;

    setTransferringId(member.id);
    try {
      await transferGroupOwnership(group.groupId, member.id);
      await fetchReport();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to transfer ownership');
    } finally {
      setTransferringId(null);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const blob = await downloadGroupsMembersReportCsv();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'group-members.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Failed to download CSV');
    } finally {
      setDownloading(false);
    }
  };

  const filteredGroups = groups
    .map((g) => ({
      ...g,
      members: search.trim()
        ? g.members.filter(
            (m) =>
              m.name.toLowerCase().includes(search.toLowerCase()) ||
              m.email.toLowerCase().includes(search.toLowerCase())
          )
        : g.members,
    }))
    .filter((g) => {
      if (!search.trim()) return true;
      return g.groupName.toLowerCase().includes(search.toLowerCase()) || g.members.length > 0;
    });

  if (loading) return <p style={styles.status}>Loading report...</p>;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h2 style={styles.title}>👥 Group Members Report</h2>
          <p style={styles.subtitle}>{groups.length} group{groups.length === 1 ? '' : 's'}, {groups.reduce((sum, g) => sum + g.members.length, 0)} total members</p>
        </div>
        <button onClick={handleDownload} style={styles.downloadBtn} disabled={downloading || groups.length === 0}>
          {downloading ? 'Downloading...' : '⬇ Download CSV'}
        </button>
      </div>

      <input
        type="text"
        placeholder="Search by group name, member name, or email..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={styles.searchInput}
      />

      {error && <p style={styles.error}>{error}</p>}

      {filteredGroups.length === 0 ? (
        <p style={styles.status}>{search ? 'No matches.' : 'No groups yet.'}</p>
      ) : (
        filteredGroups.map((g) => (
          <div key={g.groupId} style={styles.groupCard}>
            <h3 style={styles.groupName}>
              🍽️ {g.groupName} <span style={styles.memberCount}>({g.members.length} member{g.members.length === 1 ? '' : 's'})</span>
            </h3>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Name</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Role</th>
                  <th style={styles.th}>Last Login</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {g.members.map((m) => (
                  <tr key={m.id}>
                    <td style={styles.td}>
                      {m.name} {m.isAdmin && <span style={styles.adminTag}>Admin</span>}
                    </td>
                    <td style={styles.td}>{m.email}</td>
                    <td style={styles.td}>
                      <span style={m.role === 'owner' ? styles.ownerTag : styles.memberTag}>{m.role}</span>
                    </td>
                    <td style={styles.td}>
                      {m.lastLogin ? new Date(m.lastLogin).toLocaleDateString() : <span style={styles.subtle}>Never</span>}
                    </td>
                    <td style={styles.td}>
                      {m.role !== 'owner' && (
                        <button
                          onClick={() => handleTransfer(g, m)}
                          style={styles.transferBtn}
                          disabled={transferringId === m.id}
                        >
                          {transferringId === m.id ? '...' : 'Make Owner'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '900px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' },
  title: { color: '#e67e22', marginBottom: '4px' },
  subtitle: { color: '#888', fontSize: '13px' },
  downloadBtn: {
    padding: '10px 18px', backgroundColor: '#e67e22', color: 'white', border: 'none',
    borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  searchInput: {
    width: '100%', padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd',
    fontSize: '14px', marginBottom: '20px', boxSizing: 'border-box',
  },
  groupCard: {
    backgroundColor: 'white', padding: '20px 24px', borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)', marginBottom: '16px',
  },
  groupName: { color: '#333', fontSize: '16px', marginBottom: '12px' },
  memberCount: { color: '#999', fontSize: '13px', fontWeight: '400' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 10px', fontSize: '11px', color: '#999', borderBottom: '2px solid #f0f0f0', textTransform: 'uppercase' },
  td: { padding: '8px 10px', fontSize: '13px', color: '#444', borderBottom: '1px solid #f5f5f5' },
  ownerTag: { backgroundColor: '#fdf0e3', color: '#e67e22', fontSize: '11px', fontWeight: '600', padding: '2px 10px', borderRadius: '10px' },
  memberTag: { backgroundColor: '#f0f0f0', color: '#666', fontSize: '11px', fontWeight: '600', padding: '2px 10px', borderRadius: '10px' },
  adminTag: { marginLeft: '6px', fontSize: '10px', color: '#e67e22', fontWeight: '600' },
  transferBtn: {
    padding: '4px 10px', backgroundColor: 'white', color: '#e67e22', border: '1px solid #e67e22',
    borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  subtle: { color: '#bbb' },
  status: { textAlign: 'center', color: '#999' },
  error: { color: '#e74c3c', textAlign: 'center', marginBottom: '16px' },
};

export default AdminMembersReport;
